import type { LeagueBundle, Player, PlayersFile } from '../../shared/types';
import { groupRoster, lookupPlayer, summarizePicks, type TeamView } from './league';

/**
 * Where each roster sits on the win-now / rebuilding axis.
 *
 * Deliberately built from two things we can actually measure — the age of the
 * lineup a team starts, and how many future picks it holds relative to owning
 * exactly its own. A "true" contention score would weight players by dynasty
 * value, and there is no stable free source for that, so inventing a positional
 * weighting here would dress a guess up as a measurement.
 */

/** Positions whose age carries no dynasty signal at all. */
const AGELESS = new Set(['K', 'DEF']);

/** Ages that read as an asset, a prime-years contributor, and a depreciator. */
export const YOUNG_MAX = 23;
export const PRIME_MAX = 27;

export interface AgeBuckets {
  /** 23 and under. */
  young: number;
  /** 24 to 27. */
  prime: number;
  /** 28 and over. */
  old: number;
}

export interface ContentionTeam {
  view: TeamView;
  /**
   * Mean age of the starting lineup. Null when nothing in the lineup has a
   * known age (an empty offseason lineup, or a roster of nothing but defenses).
   */
  starterAge: number | null;
  /** How many starters actually contributed an age to that mean. */
  agedStarters: number;
  /** Starting slots that were empty or filled by a K/DEF. */
  skippedStarters: number;
  netPicks: number;
  heldPicks: number;
  buckets: AgeBuckets;
  /** Rostered players with a usable age — the denominator for `buckets`. */
  aged: number;
  oldest: Player | null;
  youngest: Player | null;
}

/**
 * Mean of the supplied ages, ignoring the ones Sleeper does not know.
 *
 * Coercing a null age to 0 instead would drag a ten-man average down by roughly
 * two and a half years per missing player, which lands in a range (low 20s)
 * that still looks plausible for a dynasty roster.
 */
function meanAge(players: Player[]): { mean: number | null; counted: number } {
  const ages = players
    .filter((p) => !AGELESS.has(p.position))
    .map((p) => p.age)
    .filter((a): a is number => a != null);
  if (ages.length === 0) return { mean: null, counted: 0 };
  return { mean: ages.reduce((a, b) => a + b, 0) / ages.length, counted: ages.length };
}

function bucket(players: Player[]): { buckets: AgeBuckets; aged: number } {
  const buckets: AgeBuckets = { young: 0, prime: 0, old: 0 };
  let aged = 0;
  for (const p of players) {
    if (AGELESS.has(p.position) || p.age == null) continue;
    aged += 1;
    if (p.age <= YOUNG_MAX) buckets.young += 1;
    else if (p.age <= PRIME_MAX) buckets.prime += 1;
    else buckets.old += 1;
  }
  return { buckets, aged };
}

export function buildContention(
  bundle: LeagueBundle,
  views: TeamView[],
  players: PlayersFile | null,
): ContentionTeam[] {
  return views.map((view) => {
    // Reuse groupRoster rather than re-deriving the lineup: it already knows
    // that `starters` is positionally aligned with the non-bench entries of
    // roster_positions and that an unfilled slot is the string "0".
    const groups = groupRoster(view.team, bundle.league.roster_positions, players);
    const lineup = groups.starters
      .map((s) => s.player)
      .filter((p): p is Player => p !== null);

    const { mean, counted } = meanAge(lineup);
    const ranked = lineup
      .filter((p) => !AGELESS.has(p.position) && p.age != null)
      .sort((a, b) => (b.age ?? 0) - (a.age ?? 0));

    const roster = view.team.players.map((id) => lookupPlayer(players, id));
    const { buckets, aged } = bucket(roster);
    const picks = summarizePicks(bundle.picks, view.roster_id);

    return {
      view,
      starterAge: mean,
      agedStarters: counted,
      skippedStarters: groups.starters.length - counted,
      netPicks: picks.net,
      heldPicks: picks.held.length,
      buckets,
      aged,
      oldest: ranked[0] ?? null,
      youngest: ranked[ranked.length - 1] ?? null,
    };
  });
}

// --- Quadrants -------------------------------------------------------------

export type Quadrant = 'win-now' | 'stacked' | 'young-core' | 'rebuilding';

export const QUADRANTS: Record<Quadrant, { label: string; blurb: string }> = {
  'win-now': {
    label: 'Win-Now',
    blurb: 'Veteran lineup, no pick surplus.',
  },
  stacked: {
    label: 'Stacked',
    blurb: 'Veteran lineup, extra picks.',
  },
  rebuilding: {
    label: 'Rebuilding',
    blurb: 'Young lineup, extra picks.',
  },
  'young-core': {
    label: 'Young Core',
    blurb: 'Young lineup, picks already spent.',
  },
};

/**
 * The age pivot is the league's own mean starter age, so the chart reads
 * "older/younger than everyone else" rather than against an arbitrary number
 * that would drift out of date as the league ages.
 */
export function agePivot(teams: ContentionTeam[]): number | null {
  const ages = teams.map((t) => t.starterAge).filter((a): a is number => a != null);
  if (ages.length === 0) return null;
  return ages.reduce((a, b) => a + b, 0) / ages.length;
}

export function quadrantOf(team: ContentionTeam, pivot: number): Quadrant {
  const old = (team.starterAge ?? pivot) >= pivot;
  const surplus = team.netPicks > 0;
  if (old) return surplus ? 'stacked' : 'win-now';
  return surplus ? 'rebuilding' : 'young-core';
}
